import { useState, useEffect, useRef } from 'react';
import { I } from '../components/icons';
import { StatusDot, StatusBadge, Modal, Empty, useToast } from '../components/ui';
import { useOperatorMode } from '../hooks/useOperatorMode';
import { onVisible } from '../hooks/onVisible';
import {
  fetchGlobalServices, createGlobalService, patchGlobalService,
  deleteGlobalService, restartGlobalService, fetchGlobalServiceLogs,
  fetchGlobalServiceImageTags,
  fetchChartCatalog, fetchNodes,
  ApiError,
  type GlobalService, type ChartCatalogEntry, type NodeStatus, type ImageTags,
} from '../api/client';
import type { Route } from '../types';

const GiB = 1 << 30;
const MiB = 1 << 20;

function fmtMem(b: number) {
  return b >= GiB ? `${(b / GiB).toFixed(1)} GB` : `${Math.round(b / MiB)} MB`;
}

function fmtBytes(b: number): string {
  if (b <= 0) return '0 B';
  if (b >= 1e12) return `${(b / 1e12).toFixed(1)} TB`;
  if (b >= GiB) return `${(b / GiB).toFixed(1)} GB`;
  return `${(b / MiB).toFixed(0)} MB`;
}

// ── Add service modal ────────────────────────────────────────────────────────
function AddServiceModal({ onClose, onCreated }: { onClose: () => void; onCreated: (s: GlobalService) => void }) {
  const toast = useToast();
  const [catalog, setCatalog] = useState<ChartCatalogEntry[]>([]);
  const [name, setName] = useState('');
  const [selectedChart, setSelectedChart] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchChartCatalog().then(setCatalog).catch(() => {});
  }, []);

  const entry = catalog.find(c => c.name === selectedChart);

  const onChartChange = (val: string) => {
    setSelectedChart(val);
    // auto-fill service name from chart name if still empty
    if (!name && val) setName(val);
  };

  const submit = async () => {
    if (!name.trim() || !entry) return;
    setBusy(true);
    try {
      const svc = await createGlobalService({
        name: name.trim(),
        chart: entry.chartName,
        chartVersion: entry.chartVersion,
        serviceGroup: entry.group || 'cpu',
        icon: entry.icon,
      });
      toast?.success(`Service "${svc.name}" created`);
      onCreated(svc);
    } catch (err) {
      toast?.error('Create failed', err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Add global service">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label className="field-label">Chart</label>
          <select className="select" value={selectedChart} onChange={e => onChartChange(e.target.value)}>
            <option value="">— select a chart —</option>
            {catalog.map(c => (
              <option key={c.name} value={c.name}>{c.label} · v{c.chartVersion}</option>
            ))}
          </select>
          {entry && <div className="field-hint">{entry.chartName} v{entry.chartVersion} · {entry.group} · {entry.defaultReplicas} replica{entry.defaultReplicas !== 1 ? 's' : ''} default</div>}
        </div>
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label className="field-label">Service name</label>
          <input className="input input-mono" placeholder="e.g. gpu-api" value={name} onChange={e => setName(e.target.value)}/>
          <div className="field-hint">Unique identifier — how it will appear in the list</div>
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={busy || !name.trim() || !entry} onClick={submit}>
          {busy ? 'Creating…' : 'Create'}
        </button>
      </div>
    </Modal>
  );
}

// ── Logs modal ───────────────────────────────────────────────────────────────
function LogsModal({ name, onClose }: { name: string; onClose: () => void }) {
  const [logs, setLogs] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    fetchGlobalServiceLogs(name).then(r => setLogs(r.logs)).catch(() => setLogs('Failed to fetch logs.'));
  }, [name]);
  useEffect(() => { bottomRef.current?.scrollIntoView(); }, [logs]);

  return (
    <Modal open onClose={onClose} title={`Logs — ${name}`} size="lg">
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, background: 'var(--bg-0)', borderRadius: 6, padding: '12px 14px', maxHeight: 420, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'var(--text-2)' }}>
        {logs === null ? 'Loading…' : logs || <span style={{ color: 'var(--text-3)' }}>No logs available.</span>}
        <div ref={bottomRef}/>
      </div>
    </Modal>
  );
}

// ── Service detail (page) ─────────────────────────────────────────────────────
// Renders the full detail PAGE for one global service: a page-head with the
// service name + status + action buttons (restart / logs / delete), then the
// editable settings in a card, and a save/reset footer. Reached from the list
// via /global-apps/<name>.
function ServiceDetail({ svc, onUpdated, onDeleted, onNav }: {
  svc: GlobalService;
  onUpdated: (s: GlobalService, startFastPoll?: boolean) => void;
  onDeleted: (name: string) => void;
  onNav: (r: Route) => void;
}) {
  const toast = useToast();
  const { unlocked: operator } = useOperatorMode();
  const [draft, setDraft] = useState({ ...svc });
  const [nodes, setNodes] = useState<NodeStatus[]>([]);
  const [saving, setSaving] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => { fetchNodes().then(setNodes).catch(() => {}); }, []);

  // reset draft when selected service changes
  useEffect(() => { setDraft({ ...svc }); }, [svc.name]);

  // Image-version listing — mirrors the per-service edit panel. When the chart
  // declares a listable image, the Image version field becomes a datalist
  // combobox; otherwise it stays a plain text input. Never errors hard.
  const [imageTags, setImageTags] = useState<ImageTags | null>(null);
  useEffect(() => {
    let cancelled = false;
    setImageTags(null);
    fetchGlobalServiceImageTags(svc.name)
      .then((res) => { if (!cancelled) setImageTags(res); })
      .catch(() => { if (!cancelled) setImageTags({ supported: false }); });
    return () => { cancelled = true; };
  }, [svc.name]);
  const tagListId = `global-image-tags-${svc.name}`;
  const hasTagOptions = !!imageTags?.supported && (imageTags.tags?.length ?? 0) > 0;

  const dirty = draft.replicas !== svc.replicas
    || draft.cpuLimitMilli !== svc.cpuLimitMilli
    || draft.memoryLimitBytes !== svc.memoryLimitBytes
    || (draft.nodeName ?? '') !== (svc.nodeName ?? '')
    || (draft.imageTag ?? '') !== (svc.imageTag ?? '')
    || (draft.ingressHost ?? '') !== (svc.ingressHost ?? '')
    || (draft.tlsEnabled ?? true) !== (svc.tlsEnabled ?? true);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await patchGlobalService(svc.name, {
        replicas:         draft.replicas,
        cpuLimitMilli:    draft.cpuLimitMilli,
        memoryLimitBytes: draft.memoryLimitBytes,
        nodeName:         draft.nodeName ?? '',
        imageTag:         draft.imageTag ?? '',
        ingressHost:      (draft.ingressHost ?? '').trim(),
        tlsEnabled:       draft.tlsEnabled ?? true,
      });
      onUpdated(updated);
      toast?.success('Saved');
      onNav({ page: 'global-apps' }); // back to the services list after a save
    } catch (err) {
      toast?.error('Save failed', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const restart = async () => {
    setRestarting(true);
    try {
      await restartGlobalService(svc.name);
      onUpdated({ ...svc, status: 'pending' }, true);
      toast?.success(`Restarting ${svc.name}`);
    } catch (err) {
      toast?.error('Restart failed', err instanceof ApiError ? err.message : String(err));
    } finally {
      setRestarting(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await deleteGlobalService(svc.name);
      onDeleted(svc.name);
      toast?.success(`Deleted ${svc.name}`);
    } catch (err) {
      toast?.error('Delete failed', err instanceof ApiError ? err.message : String(err));
      setDeleting(false);
    }
  };

  const memGB = draft.memoryLimitBytes / GiB;
  const cpuCores = draft.cpuLimitMilli / 1000;

  return (
    <div className="page-narrow">
      {/* Centered column so the header + settings card hug the form instead of
          stretching across the full page width. */}
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Page header — title + status on one row, actions aligned right */}
      <div className="page-head spread" style={{ alignItems: 'flex-start' }}>
        <div className="row" style={{ gap: 12, flex: 1, minWidth: 0 }}>
          <button className="btn btn-ghost btn-icon btn-sm" title="Back to global services" aria-label="Back to global services"
                  onClick={() => onNav({ page: 'global-apps' })}>
            <I.ChevronL size={15}/>
          </button>
          <div style={{ minWidth: 0 }}>
            <div className="row" style={{ gap: 10 }}>
              <h1 className="page-title" style={{ marginBottom: 0 }}>{svc.name}</h1>
              <StatusBadge status={svc.status}/>
              <span className="badge" style={{ fontSize: 11 }}>{svc.serviceGroup}</span>
            </div>
            <p className="page-sub row" style={{ marginTop: 4, gap: 14, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <span className="row" style={{ gap: 5, alignItems: 'baseline' }}>
                <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>chart</span>
                <span className="mono" style={{ color: 'var(--text-1)' }}>v{svc.chartVersion}</span>
              </span>
              <span style={{ color: 'var(--text-3)' }}>·</span>
              <span className="row" style={{ gap: 5, alignItems: 'baseline' }}>
                <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>image</span>
                <span className="mono" style={{ color: 'var(--text-1)' }}>{svc.imageTag?.trim() ? svc.imageTag : 'default'}</span>
              </span>
              <span style={{ color: 'var(--text-3)' }}>·</span>
              <span className="mono muted">shoptefy-global</span>
            </p>
          </div>
        </div>
        <div className="row" style={{ gap: 6, flexShrink: 0, marginTop: 2 }}>
          <button className="btn btn-sm btn-ghost" disabled={restarting} onClick={restart}>
            <I.Restart size={12}/> {restarting ? 'Restarting…' : 'Restart'}
          </button>
          <button className="btn btn-sm btn-ghost" onClick={() => setShowLogs(true)}>
            <I.Terminal size={12}/> Logs
          </button>
          <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)' }} onClick={() => setConfirmDelete(true)}>
            <I.Trash size={12}/> Delete
          </button>
        </div>
      </div>

      {/* Settings card */}
      <div className="card">
        <div className="card-pad">
          <div style={{ display: 'grid', gap: 18 }}>
            {/* Image version + placement. Chart version + namespace are read-only
                context and live in the page subheading, so the card holds only the
                editable settings — the two inputs align as a clean 2-col row. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                <label className="field-label">Image version <span className="muted">(optional · empty = chart default)</span></label>
                <input className="input input-mono" placeholder="chart default" value={draft.imageTag ?? ''}
                  list={hasTagOptions ? tagListId : undefined}
                  onChange={e => setDraft(d => ({ ...d, imageTag: e.target.value.trim() }))}/>
                {hasTagOptions && (
                  <datalist id={tagListId}>
                    {imageTags!.tags!.map((t) => <option key={t} value={t}/>)}
                  </datalist>
                )}
                <div className="field-hint">The actual software version running in the pod.</div>
              </div>
              <div className="field">
                <label className="field-label">Node <span className="muted">(optional)</span></label>
                <select className="select" value={draft.nodeName ?? ''} onChange={e => setDraft(d => ({ ...d, nodeName: e.target.value || undefined }))}>
                  <option value="">— any node —</option>
                  {(() => {
                    const names = nodes.map(n => n.name);
                    if (draft.nodeName && !names.includes(draft.nodeName)) names.unshift(draft.nodeName);
                    return names;
                  })().map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>

            {/* Resources — replicas, cpu, memory grouped on one row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <div className="field">
                <label className="field-label">Replicas</label>
                <div className="input-row">
                  <input type="number" className="input" min={0} value={draft.replicas}
                    onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v) && v >= 0) setDraft(d => ({ ...d, replicas: v })); }}/>
                  <span className="input-suffix">pods</span>
                </div>
              </div>
              <div className="field">
                <label className="field-label">CPU limit</label>
                <div className="input-row">
                  <input type="number" className="input" min={0.1} step="0.1" value={cpuCores}
                    onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v > 0) setDraft(d => ({ ...d, cpuLimitMilli: Math.round(v * 1000) })); }}/>
                  <span className="input-suffix">cores</span>
                </div>
              </div>
              <div className="field">
                <label className="field-label">Memory limit</label>
                <div className="input-row">
                  <input type="number" className="input" min={0.1} step="0.5" value={memGB}
                    onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v > 0) setDraft(d => ({ ...d, memoryLimitBytes: Math.round(v * GiB) })); }}/>
                  <span className="input-suffix">GB</span>
                </div>
              </div>
            </div>

            {/* Custom domain — full width */}
            <div className="field">
              <label className="field-label">Custom domain <span className="muted">(optional)</span></label>
              <input className="input input-mono" placeholder="e.g. production-data-generator.shoptefy.com"
                value={draft.ingressHost ?? ''}
                onChange={e => setDraft(d => ({ ...d, ingressHost: e.target.value }))}/>
              {(draft.ingressHost ?? '').trim() !== '' && (
                <label className="row" style={{ gap: 6, marginTop: 6, fontSize: 12.5, alignItems: 'center' }}>
                  <input type="checkbox" checked={draft.tlsEnabled ?? true}
                    onChange={e => setDraft(d => ({ ...d, tlsEnabled: e.target.checked }))}/>
                  TLS via cert-manager
                </label>
              )}
            </div>
          </div>

          {/* Disk usage (PVC volumes) — only shown when kubelet stats are available.
              Gated behind operator mode: disk numbers are inexact, hidden from the
              client view. */}
          {operator && svc.diskCapacityBytes > 0 && (
            <>
              <div className="divider"></div>
              <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
                <span style={{ color: 'var(--text-3)', marginRight: 6 }}>Disk</span>
                <span className="mono">{fmtBytes(svc.diskBytes)} / {fmtBytes(svc.diskCapacityBytes)}</span>
                <span className="muted" style={{ marginLeft: 8 }}>
                  ({Math.round(svc.diskBytes * 100 / svc.diskCapacityBytes)}%)
                </span>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="spread" style={{ padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {fmtMem(svc.memoryLimitBytes)} RAM · {(svc.cpuLimitMilli / 1000).toFixed(1)} cores · {svc.replicas} replica{svc.replicas !== 1 ? 's' : ''}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-sm" disabled={!dirty} onClick={() => setDraft({ ...svc })}>Reset</button>
            <button className="btn btn-sm btn-primary" disabled={!dirty || saving} onClick={save}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
      </div>

      {showLogs && <LogsModal name={svc.name} onClose={() => setShowLogs(false)}/>}

      {confirmDelete && (
        <Modal open onClose={() => setConfirmDelete(false)} title={`Delete "${svc.name}"?`}>
          <div style={{ padding: '4px 0 12px', color: 'var(--text-2)', fontSize: 13 }}>
            This will permanently remove the service from the database. The ArgoCD application will stop managing it.
          </div>
          <div className="modal-foot">
            <button className="btn" onClick={() => setConfirmDelete(false)}>Cancel</button>
            <button className="btn btn-danger-solid" disabled={deleting} onClick={doDelete}>
              <I.Trash size={13}/> {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Global service detail page ────────────────────────────────────────────────
// Fetches the single global service by name, shows a loader / not-found state,
// and otherwise renders the ServiceDetail page. Keeps its own 10s poll so the
// status badge stays fresh and a 3s fast poll while a rollout is in flight.
export function GlobalAppDetail({ name, onNav }: { name: string; onNav: (r: Route) => void }) {
  const [svc, setSvc] = useState<GlobalService | null>(null);
  const [loading, setLoading] = useState(true);
  const [fastPoll, setFastPoll] = useState(false);

  const refresh = (initial = false) => {
    fetchGlobalServices()
      .then(svcs => {
        const found = svcs.find(s => s.name === name) ?? null;
        setSvc(found);
        if (!initial && fastPoll && (!found || found.status !== 'pending')) {
          setFastPoll(false);
        }
      })
      .catch(() => {})
      .finally(() => { if (initial) setLoading(false); });
  };

  useEffect(() => { setLoading(true); refresh(true); /* eslint-disable-next-line */ }, [name]);

  useEffect(() => {
    const t = setInterval(() => refresh(), 10_000);
    const off = onVisible(() => refresh());
    return () => { clearInterval(t); off(); };
  }, [name, fastPoll]);

  useEffect(() => {
    if (!fastPoll) return;
    const t = setInterval(() => refresh(), 3_000);
    return () => clearInterval(t);
  }, [name, fastPoll]);

  const onUpdated = (updated: GlobalService, startFastPoll = false) => {
    setSvc(updated);
    if (startFastPoll) setFastPoll(true);
  };

  const onDeleted = () => { onNav({ page: 'global-apps' }); };

  if (loading) {
    return (
      <div className="page-narrow">
        <div style={{ padding: '40px 0', color: 'var(--text-3)', fontSize: 13, textAlign: 'center' }}>Loading…</div>
      </div>
    );
  }

  if (!svc) {
    return (
      <div className="page-narrow">
        <Empty title="Service not found" message={`No global service named "${name}".`}
               action={<button className="btn btn-primary" onClick={() => onNav({ page: 'global-apps' })}>Back to list</button>}/>
      </div>
    );
  }

  return <ServiceDetail key={svc.name} svc={svc} onUpdated={onUpdated} onDeleted={onDeleted} onNav={onNav}/>;
}

// ── List screen ───────────────────────────────────────────────────────────────
export function GlobalAppsScreen({ onNav }: { onNav: (r: Route) => void }) {
  const [services, setServices] = useState<GlobalService[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fastPoll, setFastPoll] = useState(false); // true during rollout after restart

  const refresh = (initial = false) => {
    fetchGlobalServices()
      .then(svcs => {
        setServices(svcs);
        // stop fast poll once no service is pending
        if (!initial && fastPoll && svcs.every(s => s.status !== 'pending')) {
          setFastPoll(false);
        }
      })
      .catch(() => {})
      .finally(() => { if (initial) setLoading(false); });
  };

  // Initial load
  useEffect(() => { refresh(true); }, []);

  // 10s background poll — keeps statuses fresh
  useEffect(() => {
    const t = setInterval(() => refresh(), 10_000);
    const off = onVisible(() => refresh());
    return () => { clearInterval(t); off(); };
  }, [fastPoll]);

  // 3s fast poll during rollout (after restart)
  useEffect(() => {
    if (!fastPoll) return;
    const t = setInterval(() => refresh(), 3_000);
    return () => clearInterval(t);
  }, [fastPoll]);

  const onCreated = (svc: GlobalService) => {
    setServices(prev => prev.some(s => s.name === svc.name) ? prev : [...prev, svc]);
    setShowAdd(false);
    onNav({ page: 'global-app-detail', name: svc.name });
  };

  return (
    <div className="page-narrow">
      <div className="page-head spread">
        <div>
          <h1 className="page-title">Global services</h1>
          <p className="page-sub">Services running in the <span className="mono">shoptefy-global</span> namespace, shared across all clients</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
          <I.Plus size={13}/> Add service
        </button>
      </div>

      <div className="card">
        <div className="card-head">
          <span className="muted" style={{ fontSize: 12 }}>{services.length} service{services.length === 1 ? '' : 's'}</span>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Service</th>
              <th>Chart</th>
              <th>Image</th>
              <th>Group</th>
              <th>Status</th>
              <th>Replicas</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {services.map(s => (
              <tr key={s.name} className="row-link" onClick={() => onNav({ page: 'global-app-detail', name: s.name })}>
                <td>
                  <div className="row" style={{ gap: 8 }}>
                    <StatusDot status={s.status}/>
                    <span className="mono" style={{ fontWeight: 500 }}>{s.name}</span>
                  </div>
                </td>
                <td className="mono muted" style={{ fontSize: 12 }}>{s.chart} <span style={{ color: 'var(--text-3)' }}>v{s.chartVersion}</span></td>
                <td className="mono muted" style={{ fontSize: 12 }}>{s.imageTag?.trim() ? s.imageTag : <span style={{ color: 'var(--text-3)' }}>chart default</span>}</td>
                <td><span className="badge">{s.serviceGroup}</span></td>
                <td><StatusBadge status={s.status}/></td>
                <td className="mono">{s.replicas}</td>
                <td><I.ChevronR size={14} style={{ color: 'var(--text-3)' }}/></td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && (
          <div style={{ padding: '16px 20px', color: 'var(--text-3)', fontSize: 12.5 }}>Loading…</div>
        )}
        {!loading && services.length === 0 && (
          <div style={{ padding: '20px', color: 'var(--text-3)', fontSize: 12.5 }}>No services yet. Click "Add service".</div>
        )}
      </div>

      {showAdd && <AddServiceModal onClose={() => setShowAdd(false)} onCreated={onCreated}/>}
    </div>
  );
}
